import { supabase } from './supabase';

// ── Reward Loop constants (mirror the migration defaults) ──────────────────────
export const REWARD_AMOUNT = 15;
export const REWARD_MIN_SUBTOTAL = 150;
// Program windows (owner decision 2026-07-12). All copy must import these —
// the server enforces them (Shopify endsAt + expire_stale_reward_claims()).
export const REWARD_CODE_VALID_DAYS = 60;
export const REWARD_CONFIRM_WINDOW_DAYS = 14;
export const REWARD_CLAIM_TTL_DAYS = 30;
export const MYHAIRMAIL_URL = 'https://myhairmail.com';

export type RewardClaimStatus =
  | 'pending_buyer_confirm'
  | 'buyer_confirmed'
  | 'pending_approval'
  | 'approved'
  | 'issued'
  | 'rejected'
  | 'expired'
  | 'buyer_denied';

export interface RewardClaim {
  id: string;
  listing_id: string;
  listing_title: string | null;
  seller_id: string;
  buyer_id: string;
  seller_display: string | null;
  buyer_display: string | null;
  sale_price: number | null;
  status: RewardClaimStatus;
  reward_code: string | null;
  reward_amount: number;
  reward_min_subtotal: number;
  fraud_flags: Record<string, unknown> | null;
  created_at: string;
  buyer_confirmed_at: string | null;
  issued_at: string | null;
  reward_expires_at?: string | null;
  rejected_reason: string | null;
  viewer_role: 'seller' | 'buyer';
}

export interface ListingParticipant {
  buyer_id: string;
  display_name: string;
  message_count: number;
}

/**
 * Buyer candidates for a listing the current user owns — the users who exchanged
 * messages with the seller about that listing. Server-verified (SECURITY DEFINER).
 */
export async function getListingMessageParticipants(
  listingId: string,
): Promise<{ participants: ListingParticipant[]; error: string | null }> {
  try {
    const { data, error } = await supabase.rpc('get_listing_message_participants', {
      p_listing_id: listingId,
    });
    if (error) throw error;
    return { participants: (data as ListingParticipant[]) || [], error: null };
  } catch (e: any) {
    console.error('getListingMessageParticipants error:', e);
    return { participants: [], error: e?.message || 'Could not load buyers' };
  }
}

/**
 * Seller marks a listing sold to a specific buyer → creates a pending reward claim.
 * All eligibility (ownership, thread, rate limit, fraud flags) is enforced server-side.
 */
export async function createRewardClaim(
  listingId: string,
  buyerId: string,
  salePrice?: number | null,
): Promise<{ claim: RewardClaim | null; error: string | null }> {
  try {
    const { data, error } = await supabase.rpc('create_reward_claim', {
      p_listing_id: listingId,
      p_buyer_id: buyerId,
      p_sale_price: salePrice ?? null,
    });
    if (error) throw error;
    return { claim: (data as RewardClaim) || null, error: null };
  } catch (e: any) {
    console.error('createRewardClaim error:', e);
    return { claim: null, error: e?.message || 'Could not record the sale' };
  }
}

/** Buyer confirms (or denies) a claim addressed to them. */
export async function confirmRewardClaim(
  claimId: string,
  confirm: boolean,
): Promise<{ claim: RewardClaim | null; error: string | null }> {
  try {
    const { data, error } = await supabase.rpc('confirm_reward_claim', {
      p_claim_id: claimId,
      p_confirm: confirm,
    });
    if (error) throw error;
    return { claim: (data as RewardClaim) || null, error: null };
  } catch (e: any) {
    console.error('confirmRewardClaim error:', e);
    return { claim: null, error: e?.message || 'Could not update this claim' };
  }
}

/** All of the current user's claims (both as seller and as buyer). */
export async function getMyRewardClaims(): Promise<{ claims: RewardClaim[]; error: string | null }> {
  try {
    const { data, error } = await supabase.rpc('get_my_reward_claims');
    if (error) throw error;
    return { claims: (data as RewardClaim[]) || [], error: null };
  } catch (e: any) {
    console.error('getMyRewardClaims error:', e);
    return { claims: [], error: e?.message || 'Could not load your rewards' };
  }
}

/** Admin: reject a claim with a reason (SECURITY DEFINER verifies is_admin). */
export async function rejectRewardClaim(
  claimId: string,
  reason: string,
): Promise<{ error: string | null }> {
  try {
    const { error } = await supabase.rpc('reject_reward_claim', {
      p_claim_id: claimId,
      p_reason: reason,
    });
    if (error) throw error;
    return { error: null };
  } catch (e: any) {
    console.error('rejectRewardClaim error:', e);
    return { error: e?.message || 'Could not reject this claim' };
  }
}

export interface IssueResult {
  ok: boolean;
  pending?: boolean;
  alreadyIssued?: boolean;
  status?: string;
  reward_code?: string;
  message?: string;
  error?: string;
}

/**
 * Admin: approve & issue — invokes the issue-reward-discount Edge Function, which
 * verifies is_admin server-side and creates the MyHairMail discount code. Degrades
 * gracefully (pending) if Shopify secrets are not configured. supabase.functions
 * .invoke attaches the caller's auth token automatically.
 */
export async function approveAndIssueReward(claimId: string): Promise<IssueResult> {
  try {
    // Explicitly attach the current session token. functions.invoke's internal
    // auth can go stale under this project's implicit-flow + manual setSession
    // (regular PostgREST calls still work, but the Functions client keeps the anon
    // token), which makes the Edge Function see a non-admin caller → 403.
    const { data: { session } } = await supabase.auth.getSession();
    const { data, error } = await supabase.functions.invoke('issue-reward-discount', {
      body: { claim_id: claimId },
      headers: session?.access_token
        ? { Authorization: `Bearer ${session.access_token}` }
        : undefined,
    });
    if (error) {
      // The function returns structured JSON even on non-2xx; try to surface it.
      const ctx = (error as any)?.context;
      if (ctx && typeof ctx.json === 'function') {
        try {
          const parsed = await ctx.json();
          return { ok: false, ...parsed };
        } catch { /* fall through */ }
      }
      return { ok: false, error: error.message || 'Issuance request failed' };
    }
    return (data as IssueResult) ?? { ok: false, error: 'Empty response from issuance function' };
  } catch (e: any) {
    console.error('approveAndIssueReward error:', e);
    return { ok: false, error: e?.message || 'Issuance is not available right now' };
  }
}

// ── Presentation helpers ───────────────────────────────────────────────────────
export const REWARD_STATUS_LABEL: Record<RewardClaimStatus, string> = {
  pending_buyer_confirm: 'Awaiting buyer confirmation',
  buyer_confirmed: 'Buyer confirmed',
  pending_approval: 'Pending review',
  approved: 'Approved — issuing',
  issued: 'Reward issued',
  rejected: 'Not approved',
  expired: 'Expired',
  buyer_denied: 'Buyer did not confirm',
};

export function rewardStatusBadgeClass(status: RewardClaimStatus): string {
  switch (status) {
    case 'issued':
      return 'bg-green-100 text-green-800';
    case 'approved':
    case 'buyer_confirmed':
    case 'pending_approval':
      return 'bg-blue-100 text-blue-800';
    case 'pending_buyer_confirm':
      return 'bg-yellow-100 text-yellow-800';
    case 'rejected':
    case 'buyer_denied':
    case 'expired':
      return 'bg-gray-100 text-gray-700';
    default:
      return 'bg-gray-100 text-gray-700';
  }
}
